(function initializeAsNavigation() {
  const desktopBreakpoint = 1025;
  const searchDebounceDelay = 250;
  const searchCache = new Map();
  let searchAbortController;
  let searchDebounceTimer;

  function getNavigationDrawer() {
    return document.querySelector('.as-navigation-drawer');
  }

  function resetNavigationSearch({ clearInput = true } = {}) {
    const navigationDrawer = getNavigationDrawer();
    const searchInput = navigationDrawer?.querySelector('[data-as-navigation-search-input]');
    const searchResults = navigationDrawer?.querySelector('[data-as-navigation-search-results]');

    window.clearTimeout(searchDebounceTimer);
    searchAbortController?.abort();
    searchAbortController = undefined;

    navigationDrawer?.classList.remove('is-searching', 'is-search-loading');
    if (clearInput && searchInput) searchInput.value = '';
    searchInput?.setAttribute('aria-expanded', 'false');

    if (searchResults) {
      searchResults.hidden = true;
      searchResults.innerHTML = '';
    }
  }

  function closeNavigationDrawer() {
    const navigationDrawer = getNavigationDrawer();
    const desktopMenuToggle = document.querySelector('.as-desktop-menu-toggle');

    document.body.classList.remove('menu_open');
    desktopMenuToggle?.setAttribute('aria-expanded', 'false');
    navigationDrawer?.querySelector('.as-navigation-secondary')?.classList.remove('is-open');
    resetNavigationSearch();
    navigationDrawer?.querySelectorAll('.as-navigation-primary-action').forEach((primaryAction) => {
      primaryAction.classList.remove('is-active');
      primaryAction.setAttribute('aria-expanded', 'false');
    });
  }

  function getOpenMobileSubmenu(navigationDrawer) {
    return navigationDrawer?.querySelector(
      '.as-navigation-secondary.is-open, .menu-lv-item.is-open, .menu-drawer__submenu.menu-opening'
    );
  }

  function closeMobileSubmenu(navigationDrawer) {
    const secondaryNavigation = navigationDrawer?.querySelector('.as-navigation-secondary');
    const openLegacySubmenu = navigationDrawer?.querySelector('.site-nav-mobile .menu-lv-item.is-open');
    const openMenuDrawerSubmenu = navigationDrawer?.querySelector('.menu-drawer__submenu.menu-opening');

    if (secondaryNavigation?.classList.contains('is-open')) {
      secondaryNavigation.classList.remove('is-open');
      navigationDrawer.querySelectorAll('.as-navigation-primary-action').forEach((primaryAction) => {
        primaryAction.classList.remove('is-active');
        primaryAction.setAttribute('aria-expanded', 'false');
      });
      return true;
    }

    if (openLegacySubmenu) {
      openLegacySubmenu.querySelector(':scope > .menu-dropdown .nav-title-mobile')?.click();
      return true;
    }

    if (openMenuDrawerSubmenu) {
      openMenuDrawerSubmenu.querySelector('.menu-drawer__close-button')?.click();
      return true;
    }

    return false;
  }

  function createSearchHeading(text) {
    const heading = document.createElement('h2');
    heading.className = 'as-navigation-search-heading';
    heading.textContent = text;
    return heading;
  }

  function createSuggestionLink(searchTerm, label) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    const searchParams = new URLSearchParams({
      q: searchTerm,
      'options[prefix]': 'last',
      type: 'product'
    });

    link.href = `${window.routes.search_url}?${searchParams.toString()}`;
    link.textContent = label;
    item.append(link);
    return item;
  }

  function renderNavigationSearchResults(searchTerm, searchData = null) {
    const navigationDrawer = getNavigationDrawer();
    const searchResults = navigationDrawer?.querySelector('[data-as-navigation-search-results]');

    if (!navigationDrawer || !searchResults) return;

    const resources = searchData?.resources?.results || {};
    const queries = Array.isArray(resources.queries) ? resources.queries.slice(0, 3) : [];
    const products = Array.isArray(resources.products) ? resources.products.slice(0, 3) : [];
    const content = document.createElement('div');
    const suggestionsGroup = document.createElement('section');
    const suggestionsList = document.createElement('ul');
    const productsGroup = document.createElement('section');
    const productsList = document.createElement('ul');
    const viewAllLink = document.createElement('a');
    const fallbackSuggestions = [searchTerm];

    content.className = 'as-navigation-search-content';
    suggestionsGroup.className = 'as-navigation-search-group';
    suggestionsList.className = 'as-navigation-search-suggestions';
    productsGroup.className = 'as-navigation-search-group';
    productsList.className = 'as-navigation-search-products';
    viewAllLink.className = 'as-navigation-search-view-all';

    suggestionsGroup.append(createSearchHeading(searchResults.dataset.suggestedLabel || 'Suggested'));
    if (queries.length) {
      queries.forEach((query) => {
        const label = query.text || searchTerm;
        const item = createSuggestionLink(label, label);
        const link = item.querySelector('a');
        if (query.url) link.href = query.url;
        suggestionsList.append(item);
      });
    } else {
      fallbackSuggestions.forEach((suggestion) => {
        suggestionsList.append(createSuggestionLink(suggestion, suggestion));
      });
    }
    suggestionsGroup.append(suggestionsList);
    content.append(suggestionsGroup);

    productsGroup.append(createSearchHeading(searchResults.dataset.productsLabel || 'Products'));
    if (products.length) {
      products.forEach((product) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const image = document.createElement('img');
        const featuredImage = product.featured_image;
        const imageUrl = typeof featuredImage === 'string'
          ? featuredImage
          : featuredImage?.url || product.image;

        item.className = 'as-navigation-search-product';
        link.href = product.url;
        link.setAttribute('aria-label', product.title || searchTerm);

        if (imageUrl) {
          image.src = imageUrl;
          image.alt = featuredImage?.alt || product.title || '';
          image.loading = 'lazy';
          link.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'as-navigation-search-product-placeholder';
          link.append(placeholder);
        }

        item.append(link);
        productsList.append(item);
      });
      productsGroup.append(productsList);
    } else if (searchData) {
      const emptyMessage = document.createElement('p');
      emptyMessage.className = 'as-navigation-search-empty';
      emptyMessage.textContent = (searchResults.dataset.noResultsTemplate || 'No results for __TERM__')
        .replace('__TERM__', searchTerm);
      productsGroup.append(emptyMessage);
    }
    content.append(productsGroup);

    const viewAllParams = new URLSearchParams({
      q: searchTerm,
      'options[prefix]': 'last',
      type: 'product'
    });
    viewAllLink.href = `${window.routes.search_url}?${viewAllParams.toString()}`;
    viewAllLink.textContent = (searchResults.dataset.viewAllLabel || 'View all results')
      .replace(/\s*\([^)]*\)\s*$/, '');
    content.append(viewAllLink);

    searchResults.replaceChildren(content);
    searchResults.hidden = false;
    navigationDrawer.classList.remove('is-search-loading');
  }

  function fetchNavigationSearchResults(searchTerm) {
    const navigationDrawer = getNavigationDrawer();
    const searchResults = navigationDrawer?.querySelector('[data-as-navigation-search-results]');
    const predictiveSearchUrl = window.routes?.predictive_search_url;
    const cacheKey = searchTerm.toLowerCase();

    if (!navigationDrawer || !searchResults || !predictiveSearchUrl) return;

    navigationDrawer.classList.add('is-searching', 'is-search-loading');
    searchResults.hidden = false;
    renderNavigationSearchResults(searchTerm);
    navigationDrawer.classList.add('is-search-loading');

    if (searchCache.has(cacheKey)) {
      renderNavigationSearchResults(searchTerm, searchCache.get(cacheKey));
      return;
    }

    searchAbortController?.abort();
    searchAbortController = new AbortController();

    const searchParams = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'query,product',
      'resources[limit]': '3',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last'
    });
    const jsonSearchUrl = predictiveSearchUrl.endsWith('.json')
      ? predictiveSearchUrl
      : `${predictiveSearchUrl}.json`;

    fetch(`${jsonSearchUrl}?${searchParams.toString()}`, {
      signal: searchAbortController.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        return response.json();
      })
      .then((searchData) => {
        searchCache.set(cacheKey, searchData);
        renderNavigationSearchResults(searchTerm, searchData);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;

        renderNavigationSearchResults(searchTerm, { resources: { results: {} } });
        console.error(error);
      });
  }

  function bindNavigationSearch() {
    const navigationDrawer = getNavigationDrawer();
    const searchForm = navigationDrawer?.querySelector('[data-as-navigation-search-form]');
    const searchInput = navigationDrawer?.querySelector('[data-as-navigation-search-input]');

    if (!navigationDrawer || !searchForm || !searchInput || searchForm.dataset.asSearchInitialized === 'true') {
      return;
    }

    searchForm.dataset.asSearchInitialized = 'true';

    searchInput.addEventListener('input', () => {
      const searchTerm = searchInput.value.trim();

      window.clearTimeout(searchDebounceTimer);
      searchAbortController?.abort();

      if (!searchTerm) {
        resetNavigationSearch({ clearInput: false });
        return;
      }

      navigationDrawer.classList.add('is-searching', 'is-search-loading');
      searchInput.setAttribute('aria-expanded', 'true');
      searchDebounceTimer = window.setTimeout(() => {
        fetchNavigationSearchResults(searchTerm);
      }, searchDebounceDelay);
    });

    searchForm.addEventListener('submit', (submitEvent) => {
      if (!searchInput.value.trim()) submitEvent.preventDefault();
    });
  }

  function activateSecondaryPanel(targetHandle, shouldOpenMobilePanel) {
    const navigationDrawer = getNavigationDrawer();

    if (!navigationDrawer || !targetHandle) {
      return;
    }

    navigationDrawer.querySelectorAll('.as-navigation-primary-action').forEach((primaryAction) => {
      const isActiveAction = primaryAction.dataset.asMenuTarget === targetHandle;

      primaryAction.classList.toggle('is-active', isActiveAction);
      primaryAction.setAttribute('aria-expanded', isActiveAction ? 'true' : 'false');
    });

    navigationDrawer.querySelectorAll('.as-navigation-secondary-panel').forEach((secondaryPanel) => {
      secondaryPanel.classList.toggle('is-active', secondaryPanel.dataset.asMenuPanel === targetHandle);
    });

    if (shouldOpenMobilePanel) {
      navigationDrawer.querySelector('.as-navigation-secondary')?.classList.add('is-open');
    }
  }

  function createDesktopMenuToggle() {
    const desktopMenuToggle = document.querySelector('.as-desktop-menu-toggle');

    if (!desktopMenuToggle || desktopMenuToggle.dataset.asNavigationToggleInitialized === 'true') {
      return;
    }

    desktopMenuToggle.dataset.asNavigationToggleInitialized = 'true';

    desktopMenuToggle.addEventListener('click', () => {
      document.body.classList.add('menu_open');
      desktopMenuToggle.setAttribute('aria-expanded', 'true');

      const firstPrimaryAction = getNavigationDrawer()?.querySelector('[data-as-menu-target]');
      if (firstPrimaryAction) {
        activateSecondaryPanel(firstPrimaryAction.dataset.asMenuTarget, false);
      }
    });
  }

  function bindNavigationDrawerEvents() {
    const navigationDrawer = getNavigationDrawer();

    if (!navigationDrawer || navigationDrawer.dataset.asNavigationInitialized === 'true') {
      return;
    }

    navigationDrawer.dataset.asNavigationInitialized = 'true';

    navigationDrawer.querySelectorAll('.as-navigation-close').forEach((closeButton) => {
      if (closeButton.classList.contains('as-navigation-mobile-panel-close')) {
        return;
      }

      closeButton.addEventListener('click', (clickEvent) => {
        if (window.innerWidth < desktopBreakpoint && getOpenMobileSubmenu(navigationDrawer)) {
          clickEvent.preventDefault();
          clickEvent.stopImmediatePropagation();
          closeMobileSubmenu(navigationDrawer);
          return;
        }

        closeNavigationDrawer();
      });
    });

    navigationDrawer.querySelector('.as-navigation-mobile-panel-close')?.addEventListener('click', (clickEvent) => {
      clickEvent.preventDefault();
      clickEvent.stopPropagation();

      if (navigationDrawer.classList.contains('is-searching')) {
        resetNavigationSearch();
        navigationDrawer.querySelector('[data-as-navigation-search-input]')?.focus();
        return;
      }

      navigationDrawer.querySelector('.as-navigation-secondary')?.classList.remove('is-open');
      navigationDrawer.querySelectorAll('.as-navigation-primary-action').forEach((primaryAction) => {
        primaryAction.classList.remove('is-active');
        primaryAction.setAttribute('aria-expanded', 'false');
      });
    });

    document.addEventListener('click', (clickEvent) => {
      const clickedOverlay = clickEvent.target.closest('.background-overlay');

      if (clickedOverlay && document.body.classList.contains('menu_open')) {
        clickEvent.preventDefault();
        closeNavigationDrawer();
      }
    });

    navigationDrawer.querySelectorAll('.as-navigation-primary-action').forEach((primaryAction) => {
      primaryAction.addEventListener('mouseenter', () => {
        if (window.innerWidth >= desktopBreakpoint) {
          activateSecondaryPanel(primaryAction.dataset.asMenuTarget, false);
        }
      });

      primaryAction.addEventListener('focus', () => {
        if (window.innerWidth >= desktopBreakpoint) {
          activateSecondaryPanel(primaryAction.dataset.asMenuTarget, false);
        }
      });

      primaryAction.addEventListener('click', () => {
        activateSecondaryPanel(primaryAction.dataset.asMenuTarget, window.innerWidth < desktopBreakpoint);
      });
    });

    document.addEventListener('keydown', (keyboardEvent) => {
      if (keyboardEvent.key === 'Escape' && document.body.classList.contains('menu_open')) {
        if (navigationDrawer.classList.contains('is-searching')) {
          resetNavigationSearch();
          navigationDrawer.querySelector('[data-as-navigation-search-input]')?.focus();
          return;
        }

        closeNavigationDrawer();
      }
    });
  }

  function markHomepageMobileHeader() {
    if (document.body.classList.contains('template-index')) {
      document.querySelector('.header-mobile')?.classList.add('as-home-mobile-header');
    }
  }

  function initializeNavigation() {
    createDesktopMenuToggle();
    bindNavigationDrawerEvents();
    bindNavigationSearch();
    markHomepageMobileHeader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeNavigation);
  } else {
    initializeNavigation();
  }
})();
